import React, { CSSProperties, MouseEventHandler, ReactNode } from 'react';
import { Button, Statistic, Tabs } from 'antd';
import { formatBRNumber } from '@utils/formatters';
import { useTranslation } from 'react-i18next';
import { ClearOutlined } from '@ant-design/icons';

import * as S from './styles';

export type StatusItemType = 'success' | 'processing' | 'error';

export interface StatusItemProps {
  label: string;
  value: string;
  type?: StatusItemType;
  total?: number;
  color?: string;
  children?: StatusItemProps[];
}

interface StatusMenuProps {
  handleSelectedCard: (item: StatusItemProps) => void;
  size?: 'small' | 'default';
  layout?: 'vertical' | 'horizontal';
  data?: StatusItemProps[];
  all?: {
    visible?: boolean;
    title?: string;
    onClick?: MouseEventHandler<HTMLElement>;
    total?: number;
  };
  itemActive?: string;
  style?: CSSProperties;
  additionalContent?: ReactNode;
  showIconClear?: boolean;
}

const StatusMenu = ({
  data,
  all,
  handleSelectedCard,
  itemActive,
  size,
  showIconClear,
  layout = 'horizontal',
  additionalContent,
  ...props
}: StatusMenuProps) => {
  const { t } = useTranslation('main');

  return (
    <S.StatusMenuContainer itemActive={itemActive} size={size} {...props}>
      {all?.visible && (
        <Button
          type={!itemActive ? 'primary' : 'default'}
          className="status-menu-button-all"
          onClick={all.onClick}
        >
          {all.total && (
            <span className="status-menu-all">{formatBRNumber(all.total)}</span>
          )}
          {all.title ?? t('all')}
        </Button>
      )}
      <Tabs
        activeKey={itemActive ?? undefined}
        className="status-menu-tabs"
        style={{ marginTop: 16 }}
      >
        {data?.map(({ label, value, total, color, children, type }) => (
          <Tabs.TabPane
            key={value}
            tab={
              <S.TabContainer
                className={`status-menu-card ${
                  layout === 'horizontal'
                    ? 'status-menu-card-layout-horizontal'
                    : ''
                }`}
                onClick={() => {
                  handleSelectedCard({
                    label,
                    value,
                    total,
                    color,
                    children,
                    type,
                  });
                }}
              >
                {total && (
                  <Statistic
                    precision={0}
                    value={formatBRNumber(total)}
                    valueStyle={{ color }}
                    // @ts-ignore
                    formatter={formatBRNumber}
                  />
                )}
                <span
                  className={`status-menu-title ${
                    total ? 'status-menu-title-with-total' : ''
                  }`}
                  style={{
                    color: itemActive === value ? color : '',
                  }}
                >
                  {label}
                </span>
                {children && children.length > 1 && (
                  <span
                    className="status-menu-info"
                    style={{ borderColor: color, color }}
                  >
                    {children.length}
                  </span>
                )}
                {showIconClear && itemActive === value && (
                  <Button type="link" size="small" icon={<ClearOutlined />} />
                )}
              </S.TabContainer>
            }
          />
        ))}
      </Tabs>

      {additionalContent && (
        <S.AdditionalContentContainer>
          {additionalContent}
        </S.AdditionalContentContainer>
      )}
    </S.StatusMenuContainer>
  );
};

export default StatusMenu;
